/* AZCP vehicle filter — cascade behavior.
   Reads pure helpers from window.AZCP.pure and saved-vehicle helpers from
   window.AZCP.savedVehicle. Both are populated by sibling <script type="module">
   tags that load in document order before this one. Avoiding ESM imports here
   keeps a single module instance per asset (Shopify CDN ?v= cache-busting
   otherwise creates a duplicate when imports resolve relative URLs). */
(function () {
  'use strict';
  var pure = window.AZCP && window.AZCP.pure;
  var savedVehicleApi = window.AZCP && window.AZCP.savedVehicle;
  if (!pure) return;

  var FIELDS = pure.FIELD_ORDER; // ['category','make','model','engine','year']
  var PARAM = {
    category: 'filter.p.product_type',
    make: 'filter.p.m.custom.make',
    model: 'filter.p.m.custom.model',
    engine: 'filter.p.m.custom.engine',
    year: 'filter.p.m.custom.year'
  };
  var DRAFT_KEY = 'azcp.cascadeDraft.v1';
  var DEBOUNCE_MS = 80;

  document.querySelectorAll('[data-azcp-filter]').forEach(initFilter);

  function initFilter(root) {
    var collectionUrl = root.dataset.collectionUrl;
    var sectionId = root.dataset.sectionId;
    var noAutoFill = root.dataset.noAutoFill === 'true';
    var form = root.querySelector('[data-azcp-filter-form]');
    var submitBtn = root.querySelector('.azcp-filter__submit');
    var live = root.querySelector('[data-live]');
    var selects = {};
    FIELDS.forEach(function (f) { selects[f] = root.querySelector('[data-select="' + f + '"]'); });

    var inflight = null;
    var debounceTimer = null;
    var pendingDraft = null;

    // Restore draft if any — but only when auto-fill is enabled. On collection
    // pages we want a clean cascade every visit (no leftover selection from
    // earlier sessions or sole-option auto-fills that wrote to the draft).
    if (!noAutoFill) restoreDraft();

    // Bind change handlers
    FIELDS.forEach(function (f) {
      selects[f].addEventListener('change', function () { onChange(f); });
    });

    // Initial load — fetch make options
    refresh('init');

    // Intercept submit: save vehicle, strip empty params, navigate.
    // Native form submission would include `engine=` and `year=` even when blank,
    // which Shopify treats as "match empty string" → 0 results.
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      submitBtn.classList.add('is-loading');
      saveAsVehicle();
      var v = currentValues();
      var url = new URL(form.action, window.location.origin);
      Object.keys(PARAM).forEach(function (f) { url.searchParams.delete(PARAM[f]); });
      Object.keys(v).forEach(function (f) { url.searchParams.set(PARAM[f], v[f]); });
      window.location.href = url.toString();
    });

    function onChange(field) {
      // Reset DOM downstream — clears their .value and locks them.
      // pure.applyCascadeReset is the canonical rule; flashReset enforces it on the DOM.
      var idx = FIELDS.indexOf(field);
      for (var i = idx + 1; i < FIELDS.length; i++) flashReset(FIELDS[i]);
      saveDraft();
      debounce(function () { refresh(field); });
    }

    function currentValues() {
      return FIELDS.reduce(function (a, f) {
        if (selects[f].value) a[f] = selects[f].value;
        return a;
      }, {});
    }

    function debounce(fn) {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(fn, DEBOUNCE_MS);
    }

    function flashReset(f) {
      var fieldEl = root.querySelector('[data-field="' + f + '"]');
      var sel = selects[f];
      sel.value = '';
      sel.removeAttribute('data-auto');
      fieldEl.classList.remove('azcp-filter__field--filled', 'azcp-filter__field--auto');
      fieldEl.classList.add('azcp-filter__field--reset-flash', 'azcp-filter__field--locked');
      fieldEl.setAttribute('data-locked', '');
      sel.disabled = true;
      sel.setAttribute('aria-disabled', 'true');
      setOptions(sel, [], placeholderFor(f, 0));
      setCount(f, '');
      setTimeout(function () { fieldEl.classList.remove('azcp-filter__field--reset-flash'); }, 220);
    }

    function refresh(reason) {
      var values = currentValues();
      var deepest = -1;
      for (var i = 0; i < FIELDS.length; i++) if (values[FIELDS[i]]) deepest = i;
      var fieldsToReload = FIELDS.slice(deepest + 1);
      // On init, populate the two always-available top-level fields (category + make).
      // Category is optional and independent of make, so make stays usable even
      // without a category selected.
      if (reason === 'init') fieldsToReload = ['category', 'make'];

      fieldsToReload.forEach(function (f) { setLoading(f, true); });
      if (inflight) inflight.abort();
      var controller = new AbortController();
      inflight = controller;

      var url = new URL(collectionUrl, window.location.origin);
      Object.keys(values).forEach(function (f) { url.searchParams.set(PARAM[f], values[f]); });
      url.searchParams.set('section_id', sectionId);

      fetch(url.toString(), { credentials: 'same-origin', signal: controller.signal })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var parsed = pure.parseFacets(html, PARAM);
          if (reason === 'init' && savedVehicleApi && savedVehicleApi.flagStaleIfMissing) {
            savedVehicleApi.flagStaleIfMissing(parsed.make);
          }
          var autoTriggerField = null;
          fieldsToReload.forEach(function (f) {
            var opts = parsed[f] || [];
            var selectedValue = selects[f].value;
            setOptions(selects[f], opts, placeholderFor(f, opts.length));
            setCount(f, opts.length === 0 ? '' : '(' + opts.length + ')');
            setLoading(f, false);
            // Unlock policy: make is always available; model unlocks when make is filled;
            // engine and year are optional and both unlock when model is filled (independent of each other).
            if (opts.length > 0) {
              // Category and make are always available (category is the optional
              // first step; make never locks so the cascade still works without it).
              if (f === 'category') setLocked(f, false);
              else if (f === 'make') setLocked(f, false);
              else if (f === 'model' && values.make) setLocked(f, false);
              else if ((f === 'engine' || f === 'year') && values.model) setLocked(f, false);
            }
            // Restore draft value if present and valid in the freshly-loaded options
            var draftVal = pendingDraft && pendingDraft[f];
            if (draftVal && !selectedValue && opts.some(function (o) { return o.value === draftVal; })) {
              selects[f].value = draftVal;
              root.querySelector('[data-field="' + f + '"]').classList.add('azcp-filter__field--filled');
              delete pendingDraft[f];
              autoTriggerField = f;
            }
            // Sole-option auto-fill (skip for year — too many tiny "only one year" combos)
            // Also skip entirely when the filter card opts out via data-no-auto-fill
            // (collection pages: nothing should be pre-selected by default).
            else if (opts.length === 1 && f !== 'year' && !selectedValue && !noAutoFill) {
              selects[f].value = opts[0].value;
              selects[f].setAttribute('data-auto', 'true');
              root.querySelector('[data-field="' + f + '"]').classList.add('azcp-filter__field--auto', 'azcp-filter__field--filled');
              autoTriggerField = f;
            } else if (selects[f].value) {
              root.querySelector('[data-field="' + f + '"]').classList.add('azcp-filter__field--filled');
            }
          });
          updateProgress();
          updateSubmit();
          announce(buildAnnounceMsg(parsed, fieldsToReload));
          if (autoTriggerField) onChange(autoTriggerField);
        })
        .catch(function (err) {
          if (err.name === 'AbortError') return;
          fieldsToReload.forEach(function (f) {
            setLoading(f, false);
            root.querySelector('[data-field="' + f + '"]').classList.add('azcp-filter__field--error');
          });
          announce("Couldn't load options. Change a field to retry.");
        });
    }

    function placeholderFor(field, count) {
      if (count === 0) {
        if (field === 'category') return 'No categories';
        if (field === 'engine' || field === 'year') return 'No options';
        if (field === 'model') return 'No models for this make';
        return 'No options';
      }
      if (field === 'category') return 'Choose category';
      if (field === 'make') return 'Choose make';
      if (field === 'model') return 'Choose model';
      if (field === 'engine') return 'All engines';
      if (field === 'year') return 'All years';
      return 'Choose…';
    }

    function setOptions(sel, options, placeholder) {
      var current = sel.value;
      sel.innerHTML = '';
      var ph = document.createElement('option');
      ph.value = '';
      ph.textContent = placeholder;
      sel.appendChild(ph);
      options.forEach(function (o) {
        var opt = document.createElement('option');
        opt.value = o.value;
        opt.textContent = (o.count != null) ? (o.label + ' (' + o.count + ')') : o.label;
        if (o.count === 0) opt.disabled = true;
        sel.appendChild(opt);
      });
      if (options.some(function (o) { return o.value === current; })) sel.value = current;
    }

    function setCount(field, text) {
      var el = root.querySelector('[data-count="' + field + '"]');
      if (el) el.textContent = text;
    }

    function setLoading(field, loading) {
      var fieldEl = root.querySelector('[data-field="' + field + '"]');
      if (!fieldEl) return;
      fieldEl.classList.toggle('azcp-filter__field--loading', !!loading);
    }

    function setLocked(field, locked) {
      var fieldEl = root.querySelector('[data-field="' + field + '"]');
      var sel = selects[field];
      fieldEl.classList.toggle('azcp-filter__field--locked', !!locked);
      if (locked) {
        fieldEl.setAttribute('data-locked', '');
        sel.disabled = true;
        sel.setAttribute('aria-disabled', 'true');
      } else {
        fieldEl.removeAttribute('data-locked');
        sel.disabled = false;
        sel.removeAttribute('aria-disabled');
      }
    }

    function updateProgress() {
      var values = currentValues();
      FIELDS.forEach(function (f) {
        var seg = root.querySelector('[data-segment="' + f + '"]');
        if (!seg) return;
        seg.classList.toggle('is-filled', !!values[f]);
      });
      // Mark active step (first unfilled)
      FIELDS.forEach(function (f) {
        root.querySelector('[data-field="' + f + '"]').classList.remove('azcp-filter__field--active');
      });
      for (var i = 0; i < FIELDS.length; i++) {
        if (!values[FIELDS[i]]) {
          var fEl = root.querySelector('[data-field="' + FIELDS[i] + '"]');
          if (fEl && !fEl.hasAttribute('data-locked')) fEl.classList.add('azcp-filter__field--active');
          break;
        }
      }
    }

    function updateSubmit() {
      var v = currentValues();
      submitBtn.disabled = !(v.make && v.model);
    }

    function announce(msg) { if (live) live.textContent = msg; }

    function buildAnnounceMsg(parsed, fieldsToReload) {
      if (!fieldsToReload.length) return '';
      var f = fieldsToReload[0];
      var n = parsed[f] ? parsed[f].length : 0;
      return 'Showing ' + n + ' ' + f + ' option' + (n === 1 ? '' : 's');
    }

    function saveDraft() {
      // Skip writing when auto-fill is off (collection pages) so the draft
      // doesn't leak collection-scoped selections into the global filter.
      if (noAutoFill) return;
      try {
        sessionStorage.setItem(DRAFT_KEY, JSON.stringify(currentValues()));
      } catch (e) {}
    }

    function restoreDraft() {
      try {
        var raw = sessionStorage.getItem(DRAFT_KEY);
        if (!raw) return;
        pendingDraft = JSON.parse(raw);
      } catch (e) {}
    }

    function saveAsVehicle() {
      var v = currentValues();
      if (!v.make || !v.model) return;
      var engineLabel = v.engine ? selectedOptionLabel(selects.engine) : undefined;
      if (window.AZCP && window.AZCP.savedVehicle) {
        window.AZCP.savedVehicle.set({
          make: v.make,
          model: v.model,
          engine: v.engine,
          engineLabel: engineLabel,
          year: v.year,
          savedAt: Date.now()
        });
      }
      try { sessionStorage.removeItem(DRAFT_KEY); } catch (e) {}
    }

    function selectedOptionLabel(sel) {
      var opt = sel.options[sel.selectedIndex];
      return opt ? opt.textContent : '';
    }
  }
}());
